class ProductRecommender {
  constructor(options = {}) {
    const currencySymbol = typeof currency !== 'undefined' ? currency.symbol : '$';

    this.defaults = {
      containerSelector: '.js-go-cart-recommendations',
      itemSelector: '.js-go-cart-recommendation-item',
      productUrl: '/products/',
      recommendationsUrl: '/collections/recommend-product/products.json?limit=10',
      limit: 10,
      moneyFormat: `${currencySymbol}{{amount}}`,
      excludeTypes: ['Older_version']
    };

    this.settings = { ...this.defaults, ...options };
    this.swiper = null;
    this.updateRequestId = 0;
    this.recommendationsPromise = null;
    this.carouselId = `recommendation-carousel-${Math.random().toString(36).slice(2, 9)}`;

    this.handleItemClick = this.handleItemClick.bind(this);
    this.handleCartUpdated = this.handleCartUpdated.bind(this);
    this.init();
  }

  init() {
    this.container = document.querySelector(this.settings.containerSelector);
    if (!this.container) return;

    this.container.addEventListener('click', this.handleItemClick);
    document.addEventListener('goCart:updated', this.handleCartUpdated);
    this.updateRecommendations();
  }

  handleCartUpdated(event) {
    this.updateRecommendations(event.detail?.cart || null);
  }

  async updateRecommendations(cart = null) {
    const requestId = ++this.updateRequestId;

    try {
      const currentCart = cart || await this.fetchCart();
      const recommendations = await this.fetchRecommendations();

      // Ignore an older response when a newer cart update has already started.
      if (requestId !== this.updateRequestId) return;

      const cartProductIds = new Set(
        (currentCart.items || []).map(item => String(item.product_id))
      );
      const filtered = this.filterRecommendations(recommendations, cartProductIds);

      this.renderRecommendations(filtered);
    } catch (error) {
      if (requestId !== this.updateRequestId) return;
      console.error('推荐产品加载失败:', error);
      this.clearRecommendations();
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart (${response.status})`);
    }

    return response.json();
  }

  fetchRecommendations() {
    if (this.recommendationsPromise) return this.recommendationsPromise;

    this.recommendationsPromise = fetch(this.settings.recommendationsUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to fetch products (${response.status})`);
        }
        return response.json();
      })
      .then(data => Array.isArray(data.products) ? data.products : [])
      .catch(error => {
        // Permit a later cart update to retry after a temporary network error.
        this.recommendationsPromise = null;
        throw error;
      });

    return this.recommendationsPromise;
  }

  filterRecommendations(products, cartProductIds) {
    if (!Array.isArray(products)) return [];

    return products
      .filter(product => (
        product?.id &&
        !cartProductIds.has(String(product.id)) &&
        !this.settings.excludeTypes.includes(product.product_type) &&
        product.variants?.some(variant => variant.available !== false)
      ))
      .slice(0, this.settings.limit);
  }

  clearRecommendations() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.container.replaceChildren();
    this.container.hidden = true;
  }

  renderRecommendations(products) {
    if (!products.length) {
      this.clearRecommendations();
      return;
    }

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.container.hidden = false;
    this.container.style.removeProperty('display');
    this.container.innerHTML = `
      <div class="jnc-rec-swiper-wrap">
        <div
          id="${this.carouselId}"
          class="swiper jnc-rec-swiper"
          role="region"
          aria-roledescription="carousel"
          aria-label="Product recommendations"
        >
          <div class="swiper-wrapper jnc-rec-wrapper">
            ${products.map((product, index) => (
              this.renderProduct(product, index, products.length)
            )).join('')}
          </div>
        </div>
        <div class="jnc-rec-pagination"></div>
      </div>
    `;

    const swiperEl = this.container.querySelector('.jnc-rec-swiper');
    const paginationEl = this.container.querySelector('.jnc-rec-pagination');

    if (!swiperEl || typeof Swiper === 'undefined' || products.length <= 1) {
      if (paginationEl) paginationEl.hidden = true;
      return;
    }

    const updateAriaCurrent = swiper => {
      const bullets = swiper.pagination?.bullets || [];
      bullets.forEach((bullet, index) => {
        if (index === swiper.realIndex) {
          bullet.setAttribute('aria-current', 'true');
        } else {
          bullet.removeAttribute('aria-current');
        }
      });
    };

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    this.swiper = new Swiper(swiperEl, {
      slidesPerView: 1,
      spaceBetween: 32,
      loop: true,
      speed: reduceMotion ? 0 : 850,
      grabCursor: true,
      allowTouchMove: true,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      resistanceRatio: 0.85,
      longSwipesRatio: 0.2,
      longSwipesMs: 250,
      autoplay: reduceMotion ? false : {
        delay: 3200,
        disableOnInteraction: false,
        pauseOnMouseEnter: true
      },
      pagination: {
        el: paginationEl,
        clickable: true,
        bulletClass: 'jnc-rec-bullet',
        bulletActiveClass: 'is-active',
        renderBullet: (index, className) => `
          <button
            type="button"
            class="${className}"
            aria-label="Go to recommendation ${index + 1} of ${products.length}"
            aria-controls="${this.carouselId}"
          ></button>
        `
      },
      on: {
        init: updateAriaCurrent,
        slideChange: updateAriaCurrent,
        paginationUpdate: updateAriaCurrent
      }
    });

    // Avoid moving the carousel while a keyboard user interacts with it.
    swiperEl.addEventListener('focusin', () => this.swiper?.autoplay?.stop());
    swiperEl.addEventListener('focusout', event => {
      if (!swiperEl.contains(event.relatedTarget) && !reduceMotion) {
        this.swiper?.autoplay?.start();
      }
    });
  }

  renderProduct(product, index, total) {
    const variant = product.variants?.find(item => item.available !== false);
    if (!variant) return '';

    const title = this.escapeHtml(product.title || 'Product');
    const handle = encodeURIComponent(product.handle || '');
    const imageUrl = this.escapeHtml(product.images?.[0]?.src || '');
    const priceCents = Math.round(Number.parseFloat(variant.price || '0') * 100);
    const compareAtCents = variant.compare_at_price
      ? Math.round(Number.parseFloat(variant.compare_at_price) * 100)
      : 0;
    const formatMoney = window.formatMoney || ((cents, format) => (
      format.replace('{{amount}}', (cents / 100).toFixed(2))
    ));
    const price = formatMoney(priceCents, this.settings.moneyFormat);
    const compareAtPrice = compareAtCents > priceCents
      ? `<span class="jnc-rec-compare-price">${formatMoney(compareAtCents, this.settings.moneyFormat)}</span>`
      : '';

    return `
      <div
        class="swiper-slide jnc-rec-item"
        role="group"
        aria-roledescription="slide"
        aria-label="${index + 1} of ${total}"
      >
        <div class="jnc-rec-row">
          <a href="${this.settings.productUrl}${handle}" class="jnc-rec-image-wrap">
            <img
              src="${imageUrl}"
              alt="${title}"
              class="jnc-rec-image"
              loading="lazy"
              decoding="async"
            >
          </a>

          <div class="jnc-rec-center">
            <div class="jnc-rec-title">${title}</div>
            <div class="jnc-rec-price">
              <span class="jnc-rec-price-current">${price}</span>
              ${compareAtPrice}
            </div>
          </div>

          <button
            class="btn js-go-cart-recommendation-item jnc-rec-btn"
            type="button"
            data-id="${variant.id}"
            data-name="${title}"
            data-price="${this.escapeHtml(variant.price || '0')}"
          >
            <span class="jnc-rec-btn-text btn-text">ADD</span>
            <span
              class="jnc-rec-btn-spinner btn-spinner"
              aria-hidden="true"
              style="display:none;"
            >
              <svg class="jnc-rec-spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" width="18" height="18">
                <circle class="jnc-rec-spinner-path" fill="none" stroke="#ffffff" stroke-width="6" cx="33" cy="33" r="30"></circle>
              </svg>
            </span>
          </button>
        </div>
      </div>
    `;
  }

  escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, character => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    })[character]);
  }

  handleItemClick(event) {
    const button = event.target.closest(this.settings.itemSelector);
    if (!button || !this.container.contains(button) || button.disabled) return;

    event.preventDefault();
    const variantId = button.dataset.id;
    if (!variantId) return;

    this.trackAddToCart(button);
    this.addToCart(variantId, button);
  }

  trackAddToCart(button) {
    const product = {
      ProductName: button.dataset.name || '',
      ProductID: button.dataset.id || '',
      Price: button.dataset.price || ''
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'recommendation_add_to_cart',
      product_id: product.ProductID,
      product_name: product.ProductName
    });

    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['track', 'Added to Cart', product]);
  }

  async addToCart(variantId, button) {
    const spinner = button.querySelector('.btn-spinner');
    const text = button.querySelector('.btn-text');

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-label', `Adding ${button.dataset.name || 'product'} to cart`);
    if (text) text.style.display = 'none';
    if (spinner) spinner.style.display = 'flex';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
      });

      if (!response.ok) {
        let message = `Failed to add product (${response.status})`;
        try {
          const error = await response.json();
          message = error.description || error.message || message;
        } catch (_) {
          // Retain the status-based fallback message.
        }
        throw new Error(message);
      }

      let updatedCart = null;
      if (window.goCart?.fetchCart) {
        updatedCart = await window.goCart.fetchCart();
      }

      document.dispatchEvent(new CustomEvent('goCart:updated', {
        detail: { cart: updatedCart }
      }));
      this.openCartDrawer();
    } catch (error) {
      console.error('添加产品失败:', error);
    } finally {
      // Refreshing recommendations may already have removed this button.
      if (button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-label');
        if (text) text.style.display = '';
        if (spinner) spinner.style.display = 'none';
      }
    }
  }

  openCartDrawer() {
    const footerDrawer = document.querySelector('.footer-cart-drawer');
    const cartTitle = document.querySelector('.cart-title');
    if (footerDrawer) footerDrawer.style.display = 'block';
    if (cartTitle) cartTitle.textContent = 'Your Cart';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ProductRecommender({
    containerSelector: '.cart-recommendations-top',
    productUrl: '/products/'
  });
});
